// RC10s Pass 3 · Server-backed driver typeahead.
//
// Replaces 100-driver / 1000-driver dropdowns that silently excluded
// most of the roster. Hits `GET /api/drivers?q=&page=&page_size=25`
// which (RC10p) returns the paginated envelope:
//
//   { items, page, page_size, total_items, total_pages,
//     has_previous, has_next, results, total }
//
// Behavior:
//   * Default alphabetical first page shown before the user types.
//   * 250ms debounce on `q`.
//   * "Load More" button when `has_next`.
//   * Loading / empty / error states explicitly rendered.
//   * Quit / terminated are excluded here (same policy as the legacy
//     dropdown). On-hold drivers are visible with a chip; archived
//     records would surface server-side archived flag — we rely on
//     the server to only return non-archived by default.
//
// No minimum-character requirement — the server handles `q=""` as a
// default alphabetical page. 708-driver Preview rosters remain fully
// reachable because every page_size=25 window scrolls forward via
// "Load More" without any client-side 1000-record cap.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";
import { api } from "@/lib/api";

export default function DriverTypeahead({
  value,                 // currently selected driver id (string)
  onSelect,              // (driver | null) => void
  testIdPrefix = "driver-typeahead",
  placeholder = "Search drivers by name, number, CDL, or email…",
  pageSize = 25,
  excludeStatuses = ["quit", "terminated"],
  autoFocus = false,
}) {
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const debounceRef = useRef(null);
  const requestSeqRef = useRef(0);

  // Debounce `q` → `debouncedQ`. Reset `page` to 1 on new query.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setPage(1);
      setDebouncedQ(q);
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [q]);

  // Fetch page `page` of drivers for `debouncedQ`. Replaces items on
  // page=1; appends on page>1 so Load More extends the list.
  useEffect(() => {
    const seq = ++requestSeqRef.current;
    const isAppend = page > 1;
    if (isAppend) setLoadingMore(true); else setLoading(true);
    setError(null);
    const params = new URLSearchParams();
    if (debouncedQ) params.set("q", debouncedQ);
    params.set("page", String(page));
    params.set("page_size", String(pageSize));
    api.get(`/drivers?${params.toString()}`)
      .then((r) => {
        if (seq !== requestSeqRef.current) return;
        const data = r?.data || {};
        const raw = data.items || data.results || [];
        const filtered = raw.filter((d) => {
          const s = (d.employment_status || "active").toLowerCase();
          return !excludeStatuses.includes(s);
        });
        setItems((prev) => isAppend ? [...prev, ...filtered] : filtered);
        setHasNext(
          typeof data.has_next === "boolean"
            ? data.has_next
            : (data.page * data.page_size) < data.total_items,
        );
        setTotalItems(Number(data.total_items ?? data.total ?? 0));
      })
      .catch((e) => {
        if (seq !== requestSeqRef.current) return;
        const msg = e?.response?.data?.detail?.message
          || e?.response?.data?.detail
          || e?.message
          || "Could not load drivers. Try again.";
        setError(typeof msg === "string" ? msg : "Could not load drivers.");
        if (!isAppend) setItems([]);
      })
      .finally(() => {
        if (seq === requestSeqRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      });
  }, [debouncedQ, page, pageSize, excludeStatuses]);

  // Keep selectedDriver's label displayable even when it's not in the
  // current result window — stash it the moment it's picked.
  useEffect(() => {
    if (value) {
      const found = items.find((d) => d.id === value);
      if (found) setSelectedDriver(found);
    } else {
      setSelectedDriver(null);
    }
  }, [value, items]);

  const handlePick = (d) => {
    setSelectedDriver(d);
    if (onSelect) onSelect(d);
  };
  const handleClear = () => {
    setSelectedDriver(null);
    if (onSelect) onSelect(null);
  };

  return (
    <div data-testid={testIdPrefix} className="w-full">
      {selectedDriver ? (
        <div
          data-testid={`${testIdPrefix}-selected`}
          className="flex items-center justify-between border border-coach-blue/60 rounded-sm px-2 py-1.5 bg-coach-blue/5"
        >
          <div className="min-w-0">
            <div className="text-[13px] text-carbon truncate font-medium">
              {selectedDriver.display_name || selectedDriver.id}
            </div>
            <div className="text-[11px] text-steel">
              {selectedDriver.driver_number ? `#${selectedDriver.driver_number}` : ""}
              {selectedDriver.cdl_class ? `  ·  CDL ${selectedDriver.cdl_class}` : ""}
              {selectedDriver.employment_status === "on_hold" ? "  ·  ⚠ On Hold" : ""}
            </div>
          </div>
          <button
            type="button"
            onClick={handleClear}
            data-testid={`${testIdPrefix}-clear`}
            aria-label="Clear selection"
            className="shrink-0 text-steel hover:text-carbon"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div>
          <div className="relative">
            <Search
              size={13}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-steel"
              aria-hidden="true"
            />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={placeholder}
              autoFocus={autoFocus}
              data-testid={`${testIdPrefix}-input`}
              className="w-full border border-coach-silver rounded-sm pl-7 pr-2 py-1.5 text-[13px]"
            />
          </div>
          <div
            data-testid={`${testIdPrefix}-results`}
            className="mt-1 border border-coach-silver rounded-sm bg-white max-h-56 overflow-y-auto"
          >
            {loading && (
              <div
                data-testid={`${testIdPrefix}-loading`}
                className="px-3 py-2 text-[12px] text-steel italic"
              >
                Searching…
              </div>
            )}
            {error && !loading && (
              <div
                data-testid={`${testIdPrefix}-error`}
                className="px-3 py-2 text-[12px] text-critical-red"
              >
                {error}
              </div>
            )}
            {!loading && !error && items.length === 0 && (
              <div
                data-testid={`${testIdPrefix}-empty`}
                className="px-3 py-2 text-[12px] text-steel italic"
              >
                No drivers match this search.
              </div>
            )}
            {!loading && !error && items.length > 0 && (
              <ul className="divide-y divide-coach-silver/60">
                {items.map((d) => (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => handlePick(d)}
                      data-testid={`${testIdPrefix}-option-${d.id}`}
                      className="w-full text-left px-3 py-1.5 text-[13px] hover:bg-coach-blue/5"
                    >
                      <div className="text-carbon truncate">
                        {d.display_name || d.id}
                        {d.employment_status === "on_hold" && (
                          <span
                            className="ml-2 text-[10px] uppercase tracking-widest text-amber-hold"
                            data-testid={`${testIdPrefix}-option-on-hold-${d.id}`}
                          >
                            ⚠ On Hold
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-steel">
                        {d.driver_number ? `#${d.driver_number}` : ""}
                        {d.cdl_class ? `  ·  CDL ${d.cdl_class}` : ""}
                        {d.email ? `  ·  ${d.email}` : ""}
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {hasNext && !loading && !error && (
              <div className="px-3 py-1.5 border-t border-coach-silver/60 bg-road-white">
                <button
                  type="button"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={loadingMore}
                  data-testid={`${testIdPrefix}-load-more`}
                  className="text-[12px] text-coach-blue hover:underline disabled:opacity-50"
                >
                  {loadingMore ? "Loading…" : `Load more (${items.length} of ${totalItems} shown)`}
                </button>
              </div>
            )}
            {!hasNext && !loading && !error && items.length > 0 && (
              <div
                data-testid={`${testIdPrefix}-exhausted`}
                className="px-3 py-1 text-[11px] text-steel italic border-t border-coach-silver/60 bg-road-white"
              >
                End of results — {items.length} of {totalItems} shown.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
